import { router } from "@inertiajs/react";
import { format } from "date-fns";
import { CornerDownRight, RotateCcw } from "lucide-react";
import { useState } from "react";
import { RefundModal } from "@/bizminer/Admin/Organizations/_components/RefundModal.tsx";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { PagyMetadata } from "@/types/Pagy";
import type { BizminerAdminCreditsTransaction } from "@/types/serializers";

interface Props {
  transactions: BizminerAdminCreditsTransaction[];
  organizationId: number;
  pagy: PagyMetadata;
}

const CategoryBadge = ({ category }: { category: string }) => {
  const label = category
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return (
    <Badge variant="secondary" className="text-xs font-medium uppercase">
      {label}
    </Badge>
  );
};

interface AmountCellProps {
  amount: number;
  isCredit: boolean;
}

const AmountCell = ({ amount, isCredit }: AmountCellProps) => (
  <span
    className={`text-lg font-semibold ${
      isCredit ? "text-chart-6" : "text-destructive"
    }`}
  >
    {isCredit ? "+" : ""}
    {amount.toLocaleString()}
  </span>
);

interface TransactionRowProps {
  transaction: BizminerAdminCreditsTransaction;
  organizationId: number;
  isRefundChild?: boolean;
}

const TransactionRow = ({
  transaction,
  organizationId,
  isRefundChild = false,
}: TransactionRowProps) => {
  const [showRefundModal, setShowRefundModal] = useState(false);
  const date = new Date(transaction.created_at);

  return (
    <>
      <tr className={`hover:bg-muted/50 ${isRefundChild ? "bg-muted/20" : ""}`}>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            {isRefundChild && (
              <CornerDownRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            )}
            <div>
              <div>{format(date, "MMM d, yyyy")}</div>
              <div className="text-xs">{format(date, "h:mm a")}</div>
            </div>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <AmountCell
            amount={transaction.amount}
            isCredit={transaction.is_credit}
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <CategoryBadge category={transaction.category} />
        </td>
        <td className="px-6 py-4">
          <div className="text-sm text-foreground">
            {transaction.metadata.reason || "—"}
          </div>
          {transaction.metadata.granted_by_email && (
            <div className="text-xs text-muted-foreground mt-1">
              By {transaction.metadata.granted_by_email}
            </div>
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground text-right">
          {transaction.balance_after.toLocaleString()}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right">
          {transaction.refundable && !transaction.refunded && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowRefundModal(true)}
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Refund
            </Button>
          )}
          {transaction.refunded && !transaction.refund && (
            <span className="text-xs text-muted-foreground">Refunded</span>
          )}
          {transaction.refunded && transaction.refund && (
            <span className="text-xs text-muted-foreground font-medium">
              Refunded below ↓
            </span>
          )}
        </td>
      </tr>

      {/* Render nested refund */}
      {transaction.refund && (
        <TransactionRow
          transaction={transaction.refund}
          organizationId={organizationId}
          isRefundChild={true}
        />
      )}

      {showRefundModal && (
        <RefundModal
          open={showRefundModal}
          onClose={() => setShowRefundModal(false)}
          transaction={transaction}
          organizationId={organizationId}
        />
      )}
    </>
  );
};

interface FilterBarProps {
  selectedCategory: string;
  onCategoryChange: (value: string) => void;
  totalCount: number;
  filteredCount: number;
}

const FilterBar = ({
  selectedCategory,
  onCategoryChange,
  totalCount,
  filteredCount,
}: FilterBarProps) => (
  <div className="mb-4 flex items-center justify-between">
    <div className="flex items-center gap-6">
      <div className="flex items-center gap-4">
        <span className="text-sm font-medium text-foreground">Filter by:</span>
        <Select value={selectedCategory} onValueChange={onCategoryChange}>
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="manual_grant">Manual Grant</SelectItem>
            <SelectItem value="subscription_refill">
              Subscription Refill
            </SelectItem>
            <SelectItem value="bonus">Bonus</SelectItem>
            <SelectItem value="admin_adjustment">Admin Adjustment</SelectItem>
            <SelectItem value="referral_bonus">Referral Bonus</SelectItem>
            <SelectItem value="analysis">Analysis</SelectItem>
            <SelectItem value="refund">Refund</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    <div className="text-sm text-muted-foreground">
      Showing {filteredCount} of {totalCount} transactions
    </div>
  </div>
);

interface PaginationControlsProps {
  pagy: PagyMetadata;
}

const PaginationControls = ({ pagy }: PaginationControlsProps) => {
  const navigate = (page: number) => {
    router.get(
      window.location.pathname,
      { page },
      { preserveState: true, preserveScroll: false },
    );
  };

  return (
    <div className="flex items-center justify-between px-6 py-4 border-t border-border">
      <div className="text-sm text-muted-foreground">
        Showing {pagy.from} to {pagy.to} of {pagy.count} transactions
      </div>

      <div className="flex items-center gap-2">
        <Button
          onClick={() => navigate(pagy.previous!)}
          disabled={!pagy.previous}
          variant="outline"
          size="sm"
        >
          Previous
        </Button>

        <span className="px-4 py-2 text-sm text-muted-foreground">
          Page {pagy.page} of {pagy.last}
        </span>

        <Button
          onClick={() => navigate(pagy.next!)}
          disabled={!pagy.next}
          variant="outline"
          size="sm"
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default function CreditTransactionsTable({
  transactions,
  organizationId,
  pagy,
}: Props) {
  const [selectedCategory, setSelectedCategory] = useState("all");

  const filteredTransactions =
    selectedCategory === "all"
      ? transactions
      : transactions.filter((t) => t.category === selectedCategory);

  return (
    <Card>
      <CardHeader className="border-b">
        <CardTitle className="text-lg">Credit Transactions</CardTitle>
        <CardDescription>Complete transaction history</CardDescription>
      </CardHeader>

      <CardContent className="pt-6">
        <FilterBar
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          totalCount={transactions.length}
          filteredCount={filteredTransactions.length}
        />

        {filteredTransactions.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p>No transactions found</p>
          </div>
        ) : (
          <div className="overflow-hidden border rounded-lg">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Reason / Granted By
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Balance After
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {filteredTransactions.map((tx) => (
                  <TransactionRow
                    key={tx.id}
                    transaction={tx}
                    organizationId={organizationId}
                  />
                ))}
              </tbody>
            </table>

            <PaginationControls pagy={pagy} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
